import { PYTHON_BACKEND_URL } from "../constants/backend";
import { BackendClientBase } from "./backendClientBase";
class ExternalFilesApiClient extends BackendClientBase {
    constructor() {
        super({
            serviceName: "ExternalFilesCache",
            getBaseUrl: () => PYTHON_BACKEND_URL,
            defaultHeaders: { "Content-Type": "application/json" },
            maxRetries: 1,
        });
    }
    async fetchFiles() {
        return this.requestJson("/rag/files", { method: "GET" }, { requireAuth: false });
    }
}
const externalFilesApiClient = new ExternalFilesApiClient();
class ExternalFilesCache {
    constructor() {
        this.files = [];
        this.lastFetched = 0;
        this.isFetching = false;
        this.fetchPromise = null;
    }
    async refresh() {
        if (this.isFetching && this.fetchPromise) {
            return this.fetchPromise;
        }
        this.isFetching = true;
        this.fetchPromise = this.doFetch();
        try {
            await this.fetchPromise;
        }
        finally {
            this.isFetching = false;
            this.fetchPromise = null;
        }
    }
    async doFetch() {
        try {
            const data = await externalFilesApiClient.fetchFiles();
            this.files = data.files || [];
            this.lastFetched = Date.now();
        }
        catch (error) {
            console.error("[ExternalFilesCache] Failed to refresh:", error);
            // Keep existing cache on refresh failures.
        }
    }
    getFiles() {
        return this.files;
    }
    hasFiles() {
        return this.files.length > 0;
    }
    getLastFetched() {
        return this.lastFetched;
    }
    isStaleOrEmpty(maxAgeMs = 30000) {
        if (this.lastFetched === 0) {
            return true;
        }
        if (this.files.length === 0) {
            return true;
        }
        return Date.now() - this.lastFetched > maxAgeMs;
    }
    async ensureFresh(maxAgeMs = 30000) {
        if (this.isStaleOrEmpty(maxAgeMs)) {
            await this.refresh();
        }
        return this.files;
    }
    formatAsContext() {
        if (this.files.length === 0) {
            return null;
        }
        const parts = [];
        parts.push("You have access to the following external files:");
        parts.push("<External Files>");
        for (const file of this.files) {
            const index = file.file_index >= 0 ? `[${file.file_index}]` : "[-]";
            if (file.sheet_summaries && file.sheet_summaries.length > 0) {
                const summaries = file.sheet_summaries
                    .slice(0, 4)
                    .map((sheet) => {
                    const largeTag = sheet.is_large ? " [large]" : "";
                    return `${sheet.sheet_name} (${sheet.row_count}x${sheet.column_count}${largeTag})`;
                })
                    .join("; ");
                const extra = file.sheet_summaries.length - Math.min(file.sheet_summaries.length, 4);
                parts.push(`  ${index} ${file.filename} (Excel file: ${summaries}${extra > 0 ? `; +${extra} more sheets` : ""})`);
            }
            else if (file.sheet_names && file.sheet_names.length > 0) {
                parts.push(`  ${index} ${file.filename} (Excel file with sheets: ${file.sheet_names.join(", ")})`);
            }
            else if (file.pages && file.pages > 0) {
                parts.push(`  ${index} ${file.filename} (${file.pages} pages)`);
            }
            else {
                parts.push(`  ${index} ${file.filename}`);
            }
        }
        parts.push("</External Files>");
        parts.push("");
        parts.push("File Index Mapping (use these indexes when referencing files):");
        for (const file of this.files) {
            if (file.file_index >= 0) {
                parts.push(`  ${file.file_index} -> ${file.filename}`);
            }
        }
        const excelFiles = this.files.filter((file) => /\.(xlsx|xlsm|xls)$/i.test(file.file_type || ""));
        if (excelFiles.length > 0) {
            parts.push("");
            parts.push("Excel File Structure:");
            for (const file of excelFiles) {
                const index = file.file_index >= 0 ? `[${file.file_index}]` : "[-]";
                parts.push(`  ${index} ${file.filename}`);
                if (file.sheet_summaries && file.sheet_summaries.length > 0) {
                    const sheets = file.sheet_summaries
                        .slice(0, 8)
                        .map((sheet) => `${sheet.sheet_name} (${sheet.row_count} rows x ${sheet.column_count} cols)`);
                    parts.push(`      Sheets: ${sheets.join(", ")}`);
                    const largeSheets = file.sheet_summaries.filter((sheet) => sheet.is_large);
                    if (largeSheets.length > 0) {
                        parts.push(`      Warning: large datasets in ${largeSheets.map((sheet) => sheet.sheet_name).join(", ")}`);
                    }
                }
                else if (file.sheet_names && file.sheet_names.length > 0) {
                    parts.push(`      Sheets: ${file.sheet_names.join(", ")}`);
                }
            }
        }
        parts.push("");
        parts.push("To search these files, use the ExternalFileSearchAgent with fileIndex parameters.");
        parts.push("For Excel files, you can also use the readExcel tool with the file index.");
        return parts.join("\n");
    }
    clear() {
        this.files = [];
        this.lastFetched = 0;
    }
}
export const externalFilesCache = new ExternalFilesCache();
